import React from "react";
import get from "lodash/get";
import map from "lodash/map";
import find from "lodash/find";
import convert from "convert-units";
import range from "lodash/range";
import round from "lodash/round";
import submit from "submit-form";
import moment from "moment";
import ParameterQuickEditInput from "../../../../parts/inputs/parameter-quick-edit-input";
import NewExerciseLineParameters from "../../program-builder/components/NewExerciseLineParameters";
import ActualResultsComponent from "./ActualResultsComponent";
import {
  parameterName,
  mapParameter,
} from "../../../../parts/other-exercise/CustomParametersSets";
import getStatus from "../../../../utils/status-exercises";
import convertToRiR from "../../../../utils/converters/convert-intensity";

import { PARAMETER_OPTIONS } from "../../../../constants";
import { repsValue } from "../../../../utils/reps-range";
import { getExerciseName } from "../../../../parts/modals/learn-more/blockHelpers";
import { getMovementsData } from "../../../../utils/exercise-library";
import SvgIcon from "../../../../parts/SvgIcon";
import { SearchDropdown } from "../../../../parts/inputs/search-dropdown";
import DayExerciseOptions from "./DayExerciseOptions";
import { getLoadDrop } from "../../../../utils/load-drop";

const { FREEFORM, LOAD, RPE, RPEAMRAP, REPS } = PARAMETER_OPTIONS;

const DayExerciseLineItem = ({
  handleQuickEditChange,
  _handleOpenAddSet,
  _handleCloseAddSet,
  _handleNewSetChange,
  _handleSaveNewSet,
  _handleNewMovementChange,
  set,
  newSet,
  setIndex,
  activeExercise,
  stateExercise,
  fromMetric,
  activeUser,
  withWeight,
  loadInputs,
  intensityInputs,
  localLoadInputs,
  localIntensityInputs,
  isQuickEditOpen,
  isAddingSet,
  currentRow,
  units,
  activeContextUser,
  _handleSaveNewSetForOther,
  prevHandleReorder,
  nextHandleReorder,
  isFirst,
  isLast,
  isAnySetCompleted,
  prefix,
  expanded,
  toggleExpanded,
  intensityMeasure,
  isRiR,
  editable,
  handleDuplicate,
}) => {
  const inputClassName = isQuickEditOpen ? "" : "hidden";
  const valueClassName = isQuickEditOpen ? "hidden" : "";

  if (isQuickEditOpen && stateExercise?.sets?.[setIndex]) {
    set = stateExercise.sets[setIndex];
  }

  const hideVelocitySettings = activeContextUser.hideVelocitySettings !== false;

  const isAssistant = activeExercise.type === "assistant";
  const isPrimary = activeExercise.type === "primary";
  // gets primary repmax of the exercise if exercise type is primary else
  // if secondary, use secondary rm, else if assistant just use active user's squat (RM doesn't really matter for assistant)
  // if all else fails, default to 0

  const fromRM = isPrimary
    ? `repMaximums.${activeExercise.primary}`
    : "secondaryRepMaximum";

  let defaultRepMax = get(
    activeExercise,
    fromRM,
    get(activeUser, "repMaximums.squat", 0)
  );

  // There were a bug after duplicating the exercises of a cycle, this handle it
  if (fromRM === "secondaryRepMaximum" && defaultRepMax === 0) {
    defaultRepMax = get(
      activeExercise,
      `repMaximums.${activeExercise.primary}`
    );
  }

  if (activeExercise.type == "secondary") {
    const movement =
      find(activeUser.exerciseLibrary, {
        movement: activeExercise.secondary,
      }) || {};
    defaultRepMax = movement.repMaximum || defaultRepMax;
  }

  defaultRepMax = Math.round(fromMetric(defaultRepMax, units), 0);
  const precision = units === "metric" ? 2 : 1;
  const load = set.load ? round(fromMetric(set.load, units), precision) : 0;

  const exerciseName = getExerciseName(activeExercise);

  let tabIndex = 100;
  const hasLoad = Boolean(load);
  const exId = activeExercise._id;
  const handleDeleteSet = (e) => {
    e.preventDefault();
    submit(
      `/actions/exercise/quick-edit/sets/remove/${setIndex}/${exId}/${undefined}/${activeUser._id}`
    );
  };
  // used to select the first field when adding set
  const handleFocus = (e) => {
    e.target.select();
  };

  const movements = getMovementsData(
    activeExercise,
    activeContextUser,
    activeUser
  );

  const status =
    moment(moment().subtract(1, "days").utc()).isAfter(
      moment(activeExercise.date).endOf("day").utc()
    ) || set.statusArray
      ? getStatus(set)
      : null;

  const StatusIcon =
    status === "Missed" ? (
      <SvgIcon src="/img/new-icons/info-circle.svg" noFilterOnLight={true} />
    ) : status === null ? null : (
      <SvgIcon src="/img/new-icons/tick-square.svg" noFilterOnLight={true} />
    );

  // Calendar day view, any set completed (show actual results)
  if (isAnySetCompleted) {
    const percentRM = set.load ? `${round(set.percentRepMax, 1)}` : "";
    return (
      <ActualResultsComponent
        exercise={activeExercise}
        set={set}
        percentRM={percentRM}
        units={units}
        withWeight={withWeight}
        fromMetric={fromMetric}
        idx={setIndex}
        load={hasLoad && withWeight(load, units)}
        exerciseName={exerciseName}
        prefix={prefix}
        expanded={expanded}
        toggleExpanded={toggleExpanded}
        intensityMeasure={intensityMeasure}
        isRiR={isRiR}
        hideVelocitySettings={hideVelocitySettings}
      />
    );
  }

  return (
    <>
      {setIndex === 0 && (
        <>
          <div className="name-wrapper">
            <span className="calendar-prefix">{prefix}</span>
            {isQuickEditOpen ? (
              <SearchDropdown
                valueKey="movementLabel"
                options={movements}
                handleChange={(val) =>
                  _handleNewMovementChange(val, exId, setIndex)
                }
                placeholder={exerciseName}
              />
            ) : (
              <p className="font-semibold">{exerciseName}</p>
            )}

            <div className="flex ml-auto gap-1">
              <button
                data-button-type="secondary"
                data-icon-only
                onClick={nextHandleReorder}
                disabled={isLast}
              >
                <SvgIcon src="/img/new-icons/arrow-square-down.svg" />
              </button>
              <button
                data-button-type="secondary"
                data-icon-only
                onClick={prevHandleReorder}
                disabled={isFirst}
              >
                <SvgIcon src="/img/new-icons/arrow-square-up.svg" />
              </button>
              {editable && (
                <DayExerciseOptions
                  activeExercise={activeExercise}
                  handleDuplicate={handleDuplicate}
                />
              )}
            </div>
          </div>

          <div className="day-parameters titles">
            {Array.isArray(activeExercise.parameters) ? (
              activeExercise.parameters.map((parameter, i) => (
                <span
                  key={`${parameter}-${i}`}
                  className={parameter === FREEFORM ? "invisible" : ""}
                >
                  {parameterName(parameter, isRiR)}
                </span>
              ))
            ) : (
              <>
                <span>Sets</span>
                <span>Reps</span>
                <span>{intensityMeasure}</span>
                <span>Load</span>
                {!isAssistant && <span>Intensity</span>}
              </>
            )}
            {isQuickEditOpen && <span className="add-delete-set" />}
            <span className="status">Status</span>
          </div>
        </>
      )}

      <div className="day-parameters">
        {Array.isArray(activeExercise.parameters) ? (
          activeExercise.parameters.map((elem, i) => (
            <span
              className={`${elem === FREEFORM ? "!text-left !basis-4/6" : ""} `}
              key={`${elem}_index${i}`}
            >
              <span className={valueClassName}>
                {elem === LOAD
                  ? set.loadDrop
                    ? getLoadDrop(set.loadDrop)
                    : set[mapParameter(elem)]
                    ? withWeight(
                        units === "imperial"
                          ? convert(set[mapParameter(elem)]).from("kg").to("lb")
                          : set[mapParameter(elem)]
                      )
                    : null
                  : [RPE, RPEAMRAP].includes(elem)
                  ? convertToRiR(set[mapParameter(elem)], !isRiR)
                  : elem === REPS
                  ? repsValue(set.reps, set.repsLower)
                  : set[mapParameter(elem)]}
              </span>
              <ParameterQuickEditInput
                id={`${exId}_${setIndex}_${elem.toLowerCase()}`}
                name={mapParameter(elem)}
                defaultValue={
                  elem === REPS
                    ? repsValue(set.reps, set.repsLower)
                    : set[mapParameter(elem)]
                }
                onInput={(e) => handleQuickEditChange(e, exId, setIndex)}
                parameter={elem}
                onChange={(e) => {
                  if (e && (e.target.value || e.target.value === "")) {
                    const value = e.target.value;
                    handleQuickEditChange(e, exId, setIndex);

                    setTimeout(() => {
                      if (
                        value === "AMRAP" &&
                        document.getElementById(`${exId}_${setIndex}_reps`)
                          .value === ""
                      ) {
                        handleQuickEditChange(
                          { target: { name: "reps", value: 0 } },
                          exId,
                          setIndex
                        );
                      }
                    }, 500);
                  }
                }}
                key={`${exId}_${setIndex}_rpe_${set.rpe}`}
                keyParam={`${exId}_${setIndex}_${elem.toLowerCase()}`}
                units={units}
                intensityMeasure={intensityMeasure}
                isRiR={isRiR}
                className={inputClassName}
                loadDrop={set.loadDrop}
              />
            </span>
          ))
        ) : (
          <>
            <span>
              <span className={valueClassName}>{set.sets}</span>
              <input
                id={`${exId}_${setIndex}_sets`}
                name="sets"
                defaultValue={set.sets}
                onInput={(e) =>
                  e &&
                  e.target.value &&
                  handleQuickEditChange(e, exId, setIndex)
                }
                type="number"
                step="1"
                className={inputClassName}
                key={`${exId}_${setIndex}_sets_${set.sets}`}
              />
            </span>

            <span>
              <span className={valueClassName}>
                {set.rpe === "AMRAP" && !set.reps
                  ? "AMRAP"
                  : repsValue(set.reps, set.repsLower)}
              </span>
              <input
                id={`${exId}_${setIndex}_reps`}
                name="reps"
                defaultValue={repsValue(set.reps, set.repsLower)}
                onInput={(e) =>
                  e &&
                  (e.target.value ||
                    (set.rpe === "AMRAP" && e.target.value === "")) &&
                  handleQuickEditChange(e, exId, setIndex)
                }
                className={inputClassName}
                type="text"
                key={`${exId}_${setIndex}_reps_${set.reps}`}
              />
            </span>

            <span>
              <span className={valueClassName}>
                {set.rpe === "AMRAP" && !set.reps
                  ? ""
                  : convertToRiR(set.rpe, !isRiR)}
              </span>
              <select
                id={`${exId}_${setIndex}_rpe`}
                key={`${exId}_${setIndex}_rpe_${set.rpe}`}
                className={inputClassName}
                name="rpe"
                defaultValue={set.rpe}
                onChange={(e) => {
                  if (e && (e.target.value || e.target.value === "")) {
                    const value = e.target.value;
                    handleQuickEditChange(e, exId, setIndex);

                    setTimeout(() => {
                      if (
                        value === "AMRAP" &&
                        document.getElementById(`${exId}_${setIndex}_reps`)
                          .value === ""
                      ) {
                        handleQuickEditChange(
                          { target: { name: "reps", value: 0 } },
                          exId,
                          setIndex
                        );
                      }
                    }, 500);
                  }
                }}
              >
                <option value="">{intensityMeasure}</option>

                {map(range(4, 9.5, 0.5), (i) => (
                  <option key={i} value={i + 1}>
                    {convertToRiR(i + 1, !isRiR)}
                  </option>
                ))}
                <option value="AMRAP">AMRAP</option>
              </select>
            </span>

            <span
              className={
                set.loadDrop && isQuickEditOpen && !isAssistant
                  ? "!flex-grow-[2]"
                  : ""
              }
            >
              <span className={valueClassName}>
                {set.loadDrop
                  ? getLoadDrop(set.loadDrop)
                  : hasLoad && withWeight(load, units)}
              </span>
              {set.loadDrop ? (
                <input
                  key={`${exId}_${setIndex}_${set.loadDrop}_loadDrop`}
                  name="loadDrop"
                  defaultValue={set.loadDrop}
                  onInput={(e) =>
                    e &&
                    (e.target.value || e.target.value === "") &&
                    handleQuickEditChange(e, exId, setIndex)
                  }
                  className={`${inputClassName} !text-left`}
                  type="text"
                />
              ) : (
                <input
                  id={`${exId}_${setIndex}_load`}
                  name="load"
                  defaultValue={load}
                  onInput={(e) =>
                    e &&
                    (e.target.value || e.target.value === "") &&
                    handleQuickEditChange(e, exId, setIndex)
                  }
                  className={inputClassName}
                  type="text"
                  ref={(input) =>
                    loadInputs
                      ? (loadInputs[`${exId}_${setIndex}`] = input)
                      : null
                  }
                  key={`${exId}_${setIndex}_load_${load}`}
                />
              )}
              <span className={`quick-edit-load-unit ${inputClassName}`}>
                {set.loadDrop ? "% mod." : units === "metric" ? "kg" : "lb"}
              </span>
            </span>

            {!isAssistant && (
              <span className={set.loadDrop && isQuickEditOpen ? "hidden" : ""}>
                <span className={valueClassName}>
                  {set.load ? `${round(set.percentRepMax, 1)}%` : ""}
                </span>
                <input
                  id={`${exId}_${setIndex}_percentRepMax`}
                  name="percentRepMax"
                  defaultValue={
                    set.percentRepMax ? round(set.percentRepMax, 1) : 0
                  }
                  onChange={(e) =>
                    e &&
                    e.target.value &&
                    handleQuickEditChange(e, exId, setIndex)
                  }
                  className={inputClassName}
                  type="text"
                  ref={(input) =>
                    intensityInputs
                      ? (intensityInputs[`${exId}_${setIndex}`] = input)
                      : null
                  }
                  key={`${exId}_${setIndex}_percentRepMax_${set.percentRepMax}`}
                />
              </span>
            )}
          </>
        )}

        {isQuickEditOpen && (
          <span className="add-delete-set">
            <button
              data-button-type="secondary"
              onClick={(e) => _handleOpenAddSet(e, setIndex)}
              className={isAddingSet ? "invisible" : ""}
            >
              <img
                src="/img/new-icons/add-gradient-icon.svg"
                height={24}
                width={24}
              />
            </button>
            <button
              data-button-type="secondary"
              onClick={(e) => handleDeleteSet(e)}
              className={isAddingSet ? "invisible" : ""}
            >
              <img src="/img/new-icons/red-trash.svg" height={18} width={18} />
            </button>
          </span>
        )}

        <span className="status">{status ? StatusIcon : null}</span>
      </div>

      {isQuickEditOpen &&
        isAddingSet &&
        currentRow === setIndex &&
        (Array.isArray(activeExercise.parameters) ? (
          <NewExerciseLineParameters
            newSet={newSet}
            setIndex={setIndex}
            _handleNewSetChange={_handleNewSetChange}
            exId={exId}
            activeExercise={activeExercise}
            activeUser={activeUser}
            _handleCloseAddSet={_handleCloseAddSet}
            _handleSaveNewSetForOther={_handleSaveNewSetForOther}
          />
        ) : (
          <div className="day-parameters">
            <span>
              <input
                autoFocus
                id={`new_${exId}_${setIndex}_sets`}
                name="sets"
                type="number"
                tabIndex={tabIndex++}
                defaultValue={newSet.sets}
                onFocus={(e) => handleFocus(e)}
                onInput={(e) =>
                  e && e.target.value && _handleNewSetChange(e, defaultRepMax)
                }
                step="1"
              />
            </span>
            <span>
              <input
                id={`new_${exId}_${setIndex}_reps`}
                name="reps"
                type="text"
                tabIndex={tabIndex++}
                defaultValue={newSet.reps}
                onInput={(e) =>
                  e && e.target.value && _handleNewSetChange(e, defaultRepMax)
                }
              />
            </span>
            <span>
              <select
                id={`new_${exId}_${setIndex}_rpe`}
                name="rpe"
                tabIndex={tabIndex++}
                defaultValue={newSet.rpe}
                onChange={(e) =>
                  e && e.target.value && _handleNewSetChange(e, defaultRepMax)
                }
              >
                <option value="">{intensityMeasure}</option>
                {map(range(4, 9.5, 0.5), (i) => (
                  <option key={i} value={i + 1}>
                    {convertToRiR(i + 1, !isRiR)}
                  </option>
                ))}
                <option value="AMRAP">AMRAP</option>
              </select>
            </span>
            <span>
              <input
                id={`new_${exId}_${setIndex}_load`}
                name="load"
                type="number"
                tabIndex={tabIndex++}
                defaultValue={newSet.load}
                onInput={(e) =>
                  e && e.target.value && _handleNewSetChange(e, defaultRepMax)
                }
                step=".1"
                ref={(input) =>
                  localLoadInputs ? (localLoadInputs["new"] = input) : null
                }
              />
            </span>
            {!isAssistant && (
              <span>
                <input
                  id={`new_${exId}_${setIndex}_percentRepMax`}
                  name="percentRepMax"
                  type="number"
                  tabIndex={tabIndex++}
                  defaultValue={newSet.percentRepMax}
                  onChange={(e) =>
                    e && e.target.value && _handleNewSetChange(e, defaultRepMax)
                  }
                  step=".1"
                  ref={(input) =>
                    localIntensityInputs
                      ? (localIntensityInputs["new"] = input)
                      : null
                  }
                />
              </span>
            )}

            <span className="add-delete-set">
              <button
                data-button-type="primary"
                tabIndex={tabIndex++}
                onClick={(e) =>
                  _handleSaveNewSet(
                    e,
                    set.equipped,
                    defaultRepMax,
                    activeUser,
                    exId,
                    setIndex
                  )
                }
              >
                <SvgIcon src="/img/new-icons/check.svg" noFilterOnLight />
              </button>
              <button
                data-button-type="secondary"
                onClick={(e) => _handleCloseAddSet(e)}
              >
                <SvgIcon
                  src="/img/new-icons/red-cross-20px.svg"
                  noFilterOnLight
                />
              </button>
            </span>
            <span className="status" />
          </div>
        ))}
    </>
  );
};

export default DayExerciseLineItem;
